import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

// Every internal JVC surface uses one shared theme scope. The class on <html>
// is deliberate: Radix portals render outside AppShell and must inherit the
// same design tokens as the page that opened them. CMS page preview is excluded
// because it renders the public website and must use the public design system.
const SYSTEM_PATHS = ['/login', '/activate-account', '/reset-password', '/account-security', '/kiosk', '/launcher', '/staff', '/analytics', '/bookings', '/messages', '/settings', '/admin'];
const PUBLIC_PREVIEW_PREFIX = '/admin/website/preview/';

const isSystemPath = () => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;
  if (pathname.startsWith(PUBLIC_PREVIEW_PREFIX)) return false;
  return SYSTEM_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');

  const systemRoute = isSystemPath();
  const effectiveDark = systemRoute ? context.systemIsDark : true;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('jvc-system-theme', systemRoute);
    root.classList.toggle('dark', effectiveDark);
    root.classList.toggle('light', !effectiveDark);
  }, [effectiveDark, systemRoute]);

  return { ...context, isDark: effectiveDark, isSystemRoute: systemRoute };
};

export const ThemeProvider = ({ children }) => {
  const [systemIsDark, setSystemIsDark] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    const stored = localStorage.getItem('jvc_system_theme');
    if (stored) return stored === 'dark';
    const legacy = localStorage.getItem('jv_theme');
    return legacy ? legacy === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('jvc_system_theme', systemIsDark ? 'dark' : 'light');
  }, [systemIsDark]);

  const toggleTheme = () => setSystemIsDark(prev => !prev);
  const value = useMemo(() => ({ systemIsDark, toggleTheme }), [systemIsDark]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
